"use client";

import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { fetchAuthSession } from "aws-amplify/auth";
import { useCartStore, selectTotalItems } from "@/stores/cart";
import { getAccessToken } from "@/lib/cart/client";
import { classifyUserState, type UserPaymentState } from "@/lib/auth/user-state";
import { nextAvailableDates, isoDate } from "@/lib/delivery/cutoffs";
import { isBlockedDate } from "@/lib/delivery/seasonal-blocks";
import { useAllowedDeliveryWeekdays } from "@/lib/delivery/use-available-days";
import { composeBlockedDate } from "@/lib/delivery/delivery-day-rules";
import { clearCart } from "@/lib/cart/storage";
import { CelebrationOverlay } from "@/components/celebration/CelebrationOverlay";

// Step 7 commit 16 / AC235-AC238: drag-to-confirm sticky footer on
// /catalog?view=power-buyer. Active-with-saved users can place an
// order with a single right-drag using the saved payment method +
// soonest delivery date. All other states fall back to a routed CTA
// to /checkout.

const LOCK_PCT = 0.8;
const CLICK_VS_DRAG_PX = 5;

type SlideStatus =
  | "idle"
  | "dragging"
  | "submitting"
  | "success"
  | "error"
  | "snap-back";

type Variant =
  | { kind: "active-saved" }
  | { kind: "active-no-saved" }
  | { kind: "new" }
  | { kind: "empty-cart" }
  | { kind: "no-delivery-cutoff" };

export function SlideToOrder() {
  const totalItems = useCartStore(selectTotalItems);
  const legacyCart = useCartStore((s) => s.legacyCart);
  const router = useRouter();
  // #499 Feature A: the one-tap order defaults to the soonest FL date; honor the
  // client's county/ZIP day availability so it never places on a disabled day.
  const allowedWeekdays = useAllowedDeliveryWeekdays();
  const flBlocked = useMemo(
    () => composeBlockedDate(isBlockedDate, allowedWeekdays),
    [allowedWeekdays],
  );
  const [state, setState] = useState<UserPaymentState | null>(null);
  const [dragX, setDragX] = useState(0);
  const [status, setStatus] = useState<SlideStatus>("idle");
  const [toast, setToast] = useState<{
    tone: "success" | "error";
    message: string;
  } | null>(null);
  const [celebration, setCelebration] = useState<{
    orderId: string;
    deliveryLabel: string;
  } | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const pointerOriginX = useRef<number>(0);
  const trackWidthRef = useRef<number>(0);

  useEffect(() => {
    let cancelled = false;
    fetchAuthSession()
      .then((s) =>
        classifyUserState(
          (s.tokens?.idToken?.payload ?? {}) as Record<string, string>,
        ),
      )
      .catch(() => null)
      .then((cls) => {
        if (cancelled) return;
        if (!cls) setState({ paidOrderCount: 0, savedMethod: null, classification: "new" });
        else setState(cls);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const variant = useMemo<Variant | null>(() => {
    if (state === null) return null;
    if (totalItems === 0) return { kind: "empty-cart" };
    const upcoming = nextAvailableDates(new Date(), {
      count: 1,
      isBlocked: flBlocked,
    });
    if (upcoming.length === 0) return { kind: "no-delivery-cutoff" };
    if (state.classification === "active-with-saved")
      return { kind: "active-saved" };
    if (state.classification === "active-no-saved")
      return { kind: "active-no-saved" };
    return { kind: "new" };
  }, [state, totalItems, flBlocked]);

  // Pointer event handlers attached at document level during drag so
  // the cursor can leave the button without dropping the gesture.
  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (!variant || variant.kind !== "active-saved") return;
    if (status === "submitting") return;
    pointerOriginX.current = e.clientX;
    trackWidthRef.current = trackRef.current?.offsetWidth ?? 0;
    setStatus("dragging");
    setDragX(0);
    (e.target as Element).setPointerCapture(e.pointerId);
  }

  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (status !== "dragging") return;
    const delta = e.clientX - pointerOriginX.current;
    if (delta < CLICK_VS_DRAG_PX) {
      // Below threshold; treat as no movement to allow accidental click.
      setDragX(0);
      return;
    }
    const max = Math.max(1, trackWidthRef.current - 56);
    const clamped = Math.max(0, Math.min(max, delta));
    setDragX(clamped);
  }

  async function onPointerUp() {
    if (status !== "dragging") return;
    const max = Math.max(1, trackWidthRef.current - 56);
    const pct = dragX / max;
    if (pct < LOCK_PCT) {
      setStatus("snap-back");
      setDragX(0);
      setTimeout(() => setStatus("idle"), 200);
      return;
    }
    setStatus("submitting");
    setDragX(max);
    await placeOrder();
  }

  async function placeOrder() {
    try {
      const token = await getAccessToken();
      if (!token) {
        setToast({ tone: "error", message: "Sign in to place this order." });
        setStatus("error");
        snapBackAfter();
        return;
      }
      // Default delivery date = soonest available (respecting area day availability).
      const upcoming = nextAvailableDates(new Date(), {
        count: 1,
        isBlocked: flBlocked,
      });
      const targetIso = upcoming[0]?.date ? isoDate(upcoming[0].date) : null;
      if (!targetIso) {
        setToast({ tone: "error", message: "No delivery date available." });
        setStatus("error");
        snapBackAfter();
        return;
      }
      // Pre-PUT the cart so the server has the delivery date for the order.
      useCartStore.getState().setSelectedDeliveryDate(targetIso);
      // Tiny wait to let CartSyncBoundary flush the date (1.2s debounce
      // is in play). Better: explicit sync. For now we let the server
      // read the cart it stored earlier; if delivery is missing the
      // /api/orders 400 fires the error path.
      const r = await fetch("/api/orders", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ payment: { kind: "saved" } }),
      });
      if (!r.ok) {
        const body = (await r.json().catch(() => ({}))) as { error?: string };
        // PASTE #563: a card-rail (Kurv) client pays first - nothing was
        // placed; send them through checkout to the card page.
        if (body.error === "card-payment-required") {
          window.location.assign("/checkout");
          return;
        }
        setToast({
          tone: "error",
          message:
            body.error === "no-delivery-date"
              ? "Pick a delivery date first."
              : body.error ?? "Order failed.",
        });
        setStatus("error");
        snapBackAfter();
        return;
      }
      const body = (await r.json()) as { order: { orderId: string; deliveryDate: string } };
      clearCart();
      setCelebration({
        orderId: body.order.orderId,
        deliveryLabel: body.order.deliveryDate,
      });
      setStatus("success");
      // Auto-reset slide after the celebration toast finishes.
      setTimeout(() => {
        setStatus("idle");
        setDragX(0);
        setCelebration(null);
      }, 5000);
    } catch (err) {
      setToast({
        tone: "error",
        message: err instanceof Error ? err.message : "Network error.",
      });
      setStatus("error");
      snapBackAfter();
    }
  }

  function snapBackAfter() {
    setDragX(0);
    setTimeout(() => setStatus("idle"), 500);
  }

  if (!variant) return null;
  // #468: an empty cart shows NO sticky footer (the old "Add items to your cart
  // to enable 1-tap order" bar was an obstacle). The CartBuilderBar takes over
  // once there are items; an empty cart is simply bar-free.
  if (variant.kind === "empty-cart") return null;

  return (
    <div
      data-testid="slide-to-order"
      data-variant={variant.kind}
      data-status={status}
      className="fixed inset-x-0 bottom-0 z-30 border-t border-brand-border bg-white px-4 py-3 shadow-[0_-4px_16px_rgba(0,0,0,0.08)] md:px-6"
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-2">
        {variant.kind === "active-saved" && (
          <>
            <div className="flex items-center justify-end">
              <Link
                href="/checkout"
                data-testid="slide-to-order-edit"
                className="text-[11px] font-medium text-brand-text-muted underline"
              >
                Edit details →
              </Link>
            </div>
            <div
              ref={trackRef}
              data-testid="slide-to-order-track"
              className="relative h-12 overflow-hidden rounded-full bg-brand-teal-soft"
            >
              <div
                className="absolute inset-y-0 left-0 rounded-full transition-colors"
                style={{
                  width: `${Math.max(56, dragX + 56)}px`,
                  background:
                    status === "submitting" || status === "success"
                      ? "var(--color-brand-success)"
                      : "var(--color-brand-teal)",
                  transition:
                    status === "snap-back" || status === "submitting"
                      ? "width 200ms ease-out, background-color 200ms ease-out"
                      : undefined,
                }}
              />
              <div
                data-testid="slide-to-order-thumb"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                className="absolute inset-y-1 left-1 inline-flex h-10 w-10 cursor-grab touch-none items-center justify-center rounded-full bg-white text-brand-teal shadow"
                style={{
                  transform: `translateX(${dragX}px)`,
                  transition:
                    status === "snap-back"
                      ? "transform 200ms ease-out"
                      : status === "submitting"
                        ? "transform 200ms ease-out"
                        : undefined,
                }}
              >
                →
              </div>
              <span
                className={`pointer-events-none absolute inset-0 flex items-center justify-center text-sm font-semibold ${
                  dragX > 100 ? "text-white" : "text-brand-teal-dark"
                }`}
              >
                {status === "submitting" || status === "success"
                  ? "Placing order…"
                  : `Slide to place order · ${totalItems} items · $${legacyCart.subtotal.toFixed(2)}`}
              </span>
            </div>
          </>
        )}

        {variant.kind === "active-no-saved" && (
          <button
            type="button"
            data-testid="slide-to-order-cta"
            onClick={() => router.push("/checkout")}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-brand-text px-4 text-sm font-semibold text-white"
          >
            Add payment to enable 1-tap order →
          </button>
        )}

        {variant.kind === "new" && (
          <button
            type="button"
            data-testid="slide-to-order-cta"
            onClick={() => router.push("/checkout")}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-brand-text px-4 text-sm font-semibold text-white"
          >
            Set up payment method to enable 1-tap order →
          </button>
        )}

        {variant.kind === "no-delivery-cutoff" && (
          <button
            type="button"
            data-testid="slide-to-order-cta"
            onClick={() => router.push("/checkout")}
            className="flex h-12 w-full items-center justify-center rounded-full bg-brand-text px-4 text-sm font-semibold text-white"
          >
            Pick a delivery date →
          </button>
        )}
      </div>
      {toast && toast.tone === "error" && (
        <div
          role="status"
          data-testid="slide-to-order-toast"
          data-tone="error"
          className="fixed bottom-20 right-4 z-40 max-w-sm rounded-xl bg-brand-warning px-4 py-3 text-sm text-brand-text shadow-2xl"
        >
          {toast.message}
        </div>
      )}
      {celebration && (
        <CelebrationOverlay
          variant="compact"
          orderId={celebration.orderId}
          deliveryLabel={celebration.deliveryLabel}
          onDismiss={() => setCelebration(null)}
        />
      )}
    </div>
  );
}
